import { useState } from 'react';
import { Link, useSearchParams, useParams, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import api from '@/lib/api';
import { CheckCircle, XCircle } from 'lucide-react';

// Extract the reset token from any reasonable URL shape we might
// receive. Email clients (notably Microsoft 365 Safe Links and Apple
// Mail's link rewriter) sometimes mangle the URL — we look in the
// query string first, then a path param, then the URL hash, and
// finally any param-looking key with "token" in its name. We also
// strip stray whitespace and trailing punctuation that some clients
// append.
function extractToken({ search, pathToken, hash }) {
  const sp = new URLSearchParams(search);
  // Common spellings we've seen in the wild.
  const fromQuery = sp.get('token') || sp.get('reset_token') || sp.get('resetToken') || sp.get('t');
  // Hash can carry the token if a client rewrote ? to # (rare but seen).
  let fromHash = '';
  if (hash) {
    const cleanHash = hash.replace(/^#/, '');
    if (cleanHash.includes('=')) {
      const hp = new URLSearchParams(cleanHash);
      fromHash = hp.get('token') || hp.get('t') || '';
    } else {
      // Some clients pass `#abc123` with no key.
      fromHash = cleanHash;
    }
  }
  const raw = pathToken || fromQuery || fromHash || '';
  // Trim whitespace + chars some clients append/prepend (`>`, `<`, `]`, `,`,
  // trailing punctuation). Tokens are URL-safe base64 so all of [A-Za-z0-9_-].
  return raw.trim().replace(/[^A-Za-z0-9_-]/g, '');
}

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const params = useParams();
  const location = useLocation();
  const token = extractToken({
    search: searchParams.toString(),
    pathToken: params.token,
    hash: location.hash,
  });
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(token ? 'form' : 'error');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (password !== confirmPassword) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, password });
      setStatus('success');
    } catch (err) {
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        toast.error(detail);
        if (detail.includes('expired') || detail.includes('Invalid') || detail.includes('already been used')) {
          setStatus('error');
        }
      } else {
        toast.error('Unable to connect to the server. Please try again.');
      }
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="reset-password-page" className="min-h-[80vh] flex items-center justify-center py-12 px-4">
      <div className="w-full max-w-md">
        {status === 'success' && (
          <div className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm text-center">
            <CheckCircle className="w-12 h-12 mx-auto text-[#56dfb4] mb-4" />
            <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Password Reset!</h1>
            <p className="text-slate-500 mb-6">Your password has been updated successfully.</p>
            <Link to="/login">
              <Button className="rounded-full bg-[#56dfb4] text-slate-900 hover:bg-[#3ec49b] px-8" data-testid="reset-go-login">
                Go to Login
              </Button>
            </Link>
          </div>
        )}

        {status === 'error' && (
          <div className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm text-center">
            <XCircle className="w-12 h-12 mx-auto text-red-400 mb-4" />
            <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Invalid Reset Link</h1>
            <p className="text-slate-500 mb-2">This reset link is invalid, expired, or has already been used.</p>
            <p className="text-xs text-slate-400 mb-6">If you reached this page from your email, your provider may have shortened or modified the link. Requesting a fresh link below will fix it.</p>
            <Link to="/forgot-password">
              <Button className="rounded-full bg-[#56dfb4] text-slate-900 hover:bg-[#3ec49b] px-8" data-testid="reset-request-new-btn">Request New Link</Button>
            </Link>
          </div>
        )}

        {status === 'form' && (
          <>
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Set New Password</h1>
              <p className="text-slate-500 mt-2">Enter your new password below</p>
            </div>

            <form onSubmit={handleSubmit} className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm space-y-5">
              <div>
                <Label className="text-sm mb-1.5 block">New Password</Label>
                <Input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  className="rounded-xl"
                  data-testid="reset-password-input"
                  required
                />
              </div>
              <div>
                <Label className="text-sm mb-1.5 block">Confirm New Password</Label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  className="rounded-xl"
                  data-testid="reset-confirm-input"
                  required
                />
              </div>
              <Button type="submit" disabled={loading} className="w-full rounded-full bg-[#56dfb4] text-slate-900 hover:bg-[#3ec49b] py-3 font-medium" data-testid="reset-submit-btn">
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
